import {
  AnalyticsService,
  PurchaseAnalyticsParams,
} from "../../core/analyticsService/AnalyticsService";
import { CoreError } from "../models/CoreError";

type AnalyticsParams = Record<string, unknown>;

export class AnalyticsFeature {
  private static get service(): AnalyticsService {
    return AnalyticsService.getInstance();
  }

  public static getCurrentScreenName(): string | null {
    return this.service.getCurrentScreenName();
  }

  public static getErrorCause(error: unknown): string {
    if (error instanceof CoreError) {
      return error.cause;
    }
    if (error instanceof Error) {
      return error.name || "Error";
    }
    return "Unknown";
  }

  public static async setUserId(userId: string | null): Promise<void> {
    await this.service.setUserId(userId);
  }

  public static async setUserProperty(
    name: string,
    value: string | number | boolean | null,
  ): Promise<void> {
    await this.service.setUserProperty(name, value);
  }

  public static trackUserProperty(
    name: string,
    value: string | number | boolean | null,
  ): void {
    this.setUserProperty(name, value).catch(() => undefined);
  }

  public static async setDefaultEventParameters(
    params: AnalyticsParams | null,
  ): Promise<void> {
    await this.service.setDefaultEventParameters(params);
  }

  /** Attached to every subsequent event; a null value removes that parameter. */
  public static trackDefaultEventParameters(
    params: AnalyticsParams | null,
  ): void {
    this.setDefaultEventParameters(params).catch(() => undefined);
  }

  public static async logScreenView(
    screenName: string,
    extra?: AnalyticsParams,
  ): Promise<void> {
    await this.service.logScreenView(screenName, screenName, extra);
  }

  public static trackScreenView(
    screenName: string,
    extra?: AnalyticsParams,
  ): void {
    this.logScreenView(screenName, extra).catch(() => undefined);
  }

  public static async logEvent(
    eventName: string,
    params?: AnalyticsParams,
  ): Promise<void> {
    await this.service.logEvent(eventName, params);
  }

  public static trackEvent(eventName: string, params?: AnalyticsParams): void {
    this.logEvent(eventName, params).catch(() => undefined);
  }

  public static async logPurchase(
    params: PurchaseAnalyticsParams,
  ): Promise<void> {
    await this.service.logPurchase(params);
  }

  public static trackPurchase(params: PurchaseAnalyticsParams): void {
    this.logPurchase(params).catch(() => undefined);
  }
}
